import React, {useState} from 'react';
import styles from './FilteredDetailsListWithPagination.module.css';
import {
    DefaultButton,
    DetailsList,
    IColumn,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    ITextFieldProps,
    Modal,
    PrimaryButton,
    SelectionMode,
    Stack,
    StackItem,
    TextField,
    TooltipOverflowMode
} from "@fluentui/react";
import Pagination from "../../Pagination/Pagination";
import {ICARequest} from "../../../model/ICARequest";
import {ColumnFieldName, Status, Tab} from "../ViewRequests/ViewRequestsUtils";
import strings from "../../../loc/strings";
import {ROLE_CA_END_USER} from "../../../constants/EiopaFnPConstants";
import EiopaFnPIcons from "../../EiopaFnPIcons/EiopaFnPIcons";
import {getUserInfo} from "../../../services/UserInfoService";
import {TooltipHost} from "@fluentui/react/lib/Tooltip";
import {useAuth} from "react-oidc-context";

interface FilteredDetailsListWithPaginationProps {
    items: ICARequest[];
    columns: IColumn[];
    onColumnHeaderClick: any;
    currentPage: number;
    onPageChange: any;
    itemsPerPage: number;
    onChangeItemsPerPage: any;
    totalItems: number;
    selectedTab: string;
    onCancelRequest: any;
    onSendReminder: any;
    onDeleteDraftRequest: any;
    noResultsMessage: string;
}

interface FilteredDetailsListWithPaginationState {
    isReminderModalOpen: boolean;
    isCancelModalOpen: boolean;
    isDeleteModalOpen: boolean;
    selectedRequest: ICARequest | undefined;
    justification: string | undefined;
}

function FilteredDetailsListWithPagination(props: FilteredDetailsListWithPaginationProps) {
    const [state, setState] = useState<FilteredDetailsListWithPaginationState>({
        isReminderModalOpen: false,
        isCancelModalOpen: false,
        isDeleteModalOpen: false,
        selectedRequest: undefined,
        justification: ""
    });

    const auth = useAuth();

    let userInfo = getUserInfo(auth.user?.id_token);

    const isCAEndUser: boolean = userInfo.roles.includes(ROLE_CA_END_USER);
    const maxCharLimit = 4000;

    function isActive(item: ICARequest): boolean {
        return item.status > Status.Draft && item.status < Status.Impossible;
    }

    function _requestTypeIcon(item: ICARequest) {
        if(item.isOutgoing) {
            return <EiopaFnPIcons iconName={"outgoing"} iconColor={"#A6C0D5"}/>
        } else {
            return <EiopaFnPIcons iconName={"incoming"} iconColor={"#A6C0D5"}/>
        }
    }

    function _styleStatusValue(fieldContent: string) {
        switch (Number(fieldContent)) {
            case Status.InProgress:
                return <span style={{color: "green"}}>{strings.statusInProgress}</span>;
            case Status.Postpone:
                return <span style={{color: "green"}}>{strings.statusPostpone}</span>;
            case Status.Impossible:
                return <span style={{color: "blue"}}>{strings.statusImpossible}</span>;
            case Status.Partially:
                return <span style={{color: "blue"}}>{strings.statusPartially}</span>;
            case Status.Responded:
                return <span style={{color: "blue"}}>{strings.statusResponded}</span>;
            case Status.SystemClosed:
                return <span style={{color: "black"}}>{strings.statusClosed}</span>;
            case Status.Cancelled:
                return <span style={{color: "black"}}>{strings.statusCancelled}</span>;
            default:
                return <></>
        }
    }

    function _onRenderViewRequestIcon() {
        return <EiopaFnPIcons iconName={"viewRequest"} iconColor={"#0078D4"}/>;
    }

    function _onRenderSendReminderIcon(item: ICARequest) {
        return <EiopaFnPIcons iconName={"sendReminder"} iconColor={!item.isOutgoing || !isCAEndUser? "#A19F9D" :"#0078D4"}/>
    }

    function _onRenderCancelRequestIcon(item: ICARequest) {
        return <EiopaFnPIcons iconName={"cancelRequest"} iconColor={!item.isOutgoing || !isCAEndUser? "#A19F9D" :"#0078D4"}/>
    }

    function _onRenderDeleteDraftRequestIcon() {
        return <EiopaFnPIcons iconName={"delete"} iconColor={"#0078D4"}/>
    }

    function _onRenderActionsIcon() {
       return <EiopaFnPIcons iconName={"actions"} iconColor={"#0078D4"}/>;
    }

    function _onRenderActionsMenuIcon() {
       return <></>;
    }

    function _activeRequestsActions(item: ICARequest): IContextualMenuItem[] {
        const activeRequestsActions: IContextualMenuItem[] = [];

        activeRequestsActions.push({
            className: (item.isOutgoing? 'outgoing':'incoming')+'-view-action',
            key: 'view',
            text: strings.requestActionView,
            onRenderIcon: () => _onRenderViewRequestIcon(),
            iconProps: {},
            href: "/request-for-information/" + item.encryptedId,
            target: "_blank",
            rel: 'opener',
        });

        if (isCAEndUser) {
            activeRequestsActions.push({
                className: (!item.isOnEscalationPeriod || !item.isOutgoing || !isCAEndUser? 'disabled':'enabled')+'-chase-action',
                key: 'chase',
                text: strings.requestActionSendReminder,
                onRenderIcon: () => _onRenderSendReminderIcon(item),
                disabled: !item.isOnEscalationPeriod || !item.isOutgoing,
                onClick: () => setState({...state, isReminderModalOpen: true, selectedRequest: item})
            });
            activeRequestsActions.push({
                className: (!item.isOutgoing || !isCAEndUser? 'disabled':'enabled')+'-cancel-action',
                key: 'cancel',
                text: strings.requestActionCancel,
                onRenderIcon: () => _onRenderCancelRequestIcon(item),
                disabled: !item.isOutgoing,
                onClick: () => setState({...state, isCancelModalOpen: true, selectedRequest: item})
            });
        }

        return activeRequestsActions;
    }

    function _outgoingRequestsActions(item: ICARequest): IContextualMenuItem[] {
        const outgoingRequestsActions: IContextualMenuItem[] = [];

        outgoingRequestsActions.push({
            className: 'outgoing-view-action',
            key: 'view',
            text: strings.requestActionView,
            onRenderIcon: () => _onRenderViewRequestIcon(),
            iconProps: {},
            href: "/request-for-information/" + item.encryptedId,
            target: "_blank",
            rel: 'opener',
        });

        if (isCAEndUser) {
            outgoingRequestsActions.push({
                className: (!item.isOnEscalationPeriod || !isCAEndUser || !isActive(item)? 'disabled':'enabled')+'-chase-action',
                key: 'chase',
                text: strings.requestActionSendReminder,
                onRenderIcon: () => _onRenderSendReminderIcon(item),
                disabled: !item.isOnEscalationPeriod || !isActive(item),
                onClick: () => setState({...state, isReminderModalOpen: true, selectedRequest: item})
            });
            outgoingRequestsActions.push({
                className: (!isCAEndUser || !isActive(item)? 'disabled':'enabled')+'-cancel-action',
                key: 'cancel',
                text: strings.requestActionCancel,
                onRenderIcon: () => _onRenderCancelRequestIcon(item),
                disabled: !isActive(item),
                onClick: () => setState({...state, isCancelModalOpen: true, selectedRequest: item})
            });
        }

        return outgoingRequestsActions;
    }

    function _incomingRequestsActions(item: ICARequest): IContextualMenuItem[] {
        return [
            {
                className: 'incoming-view-action',
                key: 'view',
                text: strings.requestActionView,
                onRenderIcon: () => _onRenderViewRequestIcon(),
                iconProps: {},
                href: "/request-for-information/" + item.encryptedId,
                target: "_blank",
                rel: 'opener',
            }
        ];
    }

    function _draftRequestsActions(item: ICARequest): IContextualMenuItem[] {
        return [
            {
                className: 'draft-view-action',
                key: 'view',
                text: strings.requestActionView,
                onRenderIcon: () => _onRenderViewRequestIcon(),
                iconProps: {},
                href: `/new-request-for-information/${item?.poiIdEncrypted}?reqId=${item?.encryptedId}`,
                target: '_blank',
                rel: 'opener',
            },
            {
                className: 'draft-delete-action',
                key: 'delete',
                text: strings.requestActionDelete,
                onRenderIcon: () => _onRenderDeleteDraftRequestIcon(),
                iconProps: {},
                onClick: () => setState({...state, isDeleteModalOpen: true, selectedRequest: item})
            }
        ];
    }

    function _actionsButton(item: ICARequest) {

        const menuProps = (): IContextualMenuProps => {
            switch (props.selectedTab) {
                case Tab.Active:
                    return {
                        items: _activeRequestsActions(item),
                        directionalHintFixed: false,
                        className: styles.actionsPanel
                    } as IContextualMenuProps;
                case Tab.Outgoing:
                    return {
                        items: _outgoingRequestsActions(item),
                        directionalHintFixed: false,
                        className: styles.actionsPanel
                    } as IContextualMenuProps;
                case Tab.Incoming:
                    return {
                        items: _incomingRequestsActions(item),
                        directionalHintFixed: false,
                        className: styles.actionsPanel
                    }
                case Tab.Draft:
                    return {
                        items: _draftRequestsActions(item),
                        directionalHintFixed: false,
                        className: styles.actionsPanel
                    }
                default:
                    return {
                        items: [],
                        directionalHintFixed: false,
                        className: styles.actionsPanel
                    } as IContextualMenuProps;
            }
        };

        return (
            <IconButton menuProps={menuProps()}
                        onRenderIcon={_onRenderActionsIcon}
                        onRenderMenuIcon={_onRenderActionsMenuIcon}
                        title="Actions"
                        ariaLabel="Actions"
                        className={(item.isOutgoing? 'outgoing':'incoming')+'-actions-button'}
            />
        )
    }

    function _remainingDays(item: ICARequest, fieldContent:string) {
        if(item.isOnEscalationPeriod) {
            return <span>{fieldContent} <EiopaFnPIcons iconName={"warning"} iconColor={"#D83B01"}/></span>
        } else {
            return <span>{fieldContent}</span>;
        }
    }

    function _requestedAuthorities(fieldContent: string) {
        if(props.selectedTab === Tab.Draft) {
            return <TooltipHost content={fieldContent}>{fieldContent}</TooltipHost>;
        } else {
            return <span>{fieldContent}</span>;
        }
    }

    function _renderItemColumn(item?: ICARequest, index?: number, column?: IColumn) {
        if(item && column) {

            if(column.fieldName == ColumnFieldName.Actions) {
                return _actionsButton(item);
            }

            const fieldContent = item[column.fieldName as keyof ICARequest] as string;
            let cellContent;
            switch (column.fieldName) {
                case ColumnFieldName.IsOutgoing:
                    cellContent = _requestTypeIcon(item);
                    break;
                case ColumnFieldName.Authority:
                    cellContent = _requestedAuthorities(fieldContent);
                    break;
                case ColumnFieldName.RequestNo:
                    cellContent = <span><b>{fieldContent}</b></span>;
                    break;
                case ColumnFieldName.Status:
                    cellContent = _styleStatusValue(fieldContent);
                    break;
                case ColumnFieldName.RemainingDays:
                    cellContent = _remainingDays(item, fieldContent);
                    break;
                default:
                    cellContent = <span>{fieldContent}</span>;
                    break;
            }
            return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={cellContent}>{cellContent}</TooltipHost>
        }
    }

    function _onChangeJustification(
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) {
        if (!newValue || newValue.length <= maxCharLimit) {
            setState({...state, justification: newValue});
        }
    }

    const _onClickConfirmCancel = () => {
        const justification = state.justification;
        setState({
            ...state,
            isCancelModalOpen: false,
            justification: ""
        });
        props.onCancelRequest(state.selectedRequest?.id, justification);
    }

    const _onSendReminder = () => {
        setState({
            ...state,
            isReminderModalOpen: false
        });
        props.onSendReminder(state.selectedRequest?.encryptedId);
    }

    const _onDeleteDraftRequest = () => {
        setState({
            ...state,
            isDeleteModalOpen: false
        });
        props.onDeleteDraftRequest(state.selectedRequest?.encryptedId);
    }

    const onRenderSuffix = (props?: ITextFieldProps | undefined) => {
        return  <div>{maxCharLimit - (props?.value?.length ?? 0)}</div>
    };

    const addTooltipToColumnHeader = (cols: IColumn[]) => {
        cols.forEach(col => {
            col.onRenderHeader= (props) => {
               return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={props?.column.name}>{props?.column.name}</TooltipHost>
            }
        });
        return cols;
    };

    return (
        <Stack>
            <StackItem>
                {props.totalItems === 0?
                    props.noResultsMessage
                :
                    <DetailsList
                        layoutMode={1}
                        selectionMode={SelectionMode.none}
                        items={props.items}
                        columns={addTooltipToColumnHeader(props.columns)}
                        onColumnHeaderClick={props.onColumnHeaderClick}
                        onRenderItemColumn={_renderItemColumn}
                    />
                }
            </StackItem>
            <StackItem>
                <Pagination
                    currentPage={props.currentPage}
                    onPageChange={props.onPageChange}
                    itemsPerPage={props.itemsPerPage}
                    onChangeItemsPerPage={props.onChangeItemsPerPage}
                    totalItems={props.totalItems}
                />
            </StackItem>
            <Modal
                isOpen={state.isReminderModalOpen}
                onDismiss={() => setState({...state, isReminderModalOpen: false})}
            >
                <Stack className={styles.ReminderModalContainer}>
                    <StackItem className={styles.ReminderModalTitleRow}>
                        <div>
                            <span className={styles.ReminderModalTitle}>
                                {strings.requestModalSendReminderTitle}
                            </span>
                            <span className={styles.ReminderModalSubTitle}>
                                ({strings.requestModalSendReminderReqNoTitle}: {state.selectedRequest?.requestNo})
                            </span>
                        </div>
                        <IconButton
                            style={{marginLeft: '40px'}}
                            iconProps={{iconName: "Cancel"}}
                            ariaLabel="Close popup modal"
                            onClick={() => setState({...state, isReminderModalOpen: false})}
                        />
                    </StackItem>
                    <StackItem className={styles.ReminderModalTextRow}>
                        <span>
                            {strings.requestModalSendReminderBody}
                        </span>
                    </StackItem>
                    <StackItem>
                        <span>
                             {strings.requestModalSendReminderBody2}
                        </span>
                    </StackItem>
                    <StackItem className={styles.ReminderModalButtons}>
                        <PrimaryButton text={strings.requestModalButtonConfirm} onClick={_onSendReminder}/>
                        <DefaultButton text={strings.requestModalButtonCancel} onClick={() => {setState({...state, isReminderModalOpen: false})}}/>
                    </StackItem>
                </Stack>
            </Modal>
            <Modal
                isOpen={state.isCancelModalOpen}
                onDismiss={() => setState({...state, isCancelModalOpen: false})}
                className={styles.CancelModal}
            >
                <Stack className={styles.CancelModalContainer}>
                    <StackItem className={styles.CancelModalTitleRow}>
                        <div>
                            <span className={styles.CancelModalTitle}>
                                Cancel request
                            </span>
                            <span className={styles.CancelModalSubTitle}>
                                (Request no: {state.selectedRequest?.requestNo})
                            </span>
                        </div>
                        <IconButton
                            style={{marginLeft: '40px'}}
                            iconProps={{iconName: "Cancel"}}
                            ariaLabel="Close popup modal"
                            onClick={() => setState({...state, isCancelModalOpen: false, justification: ""})}
                        />
                    </StackItem>
                    <StackItem className={styles.ReminderModalTextRow}>
                        <span>
                            {strings.requestModalCancelBody}
                        </span>
                    </StackItem>
                    <StackItem className={styles.CancelModalTextField}>
                        <TextField
                            label={strings.requestModalJustificationLabel}
                            ariaLabel={strings.requestModalJustificationLabel}
                            value={state.justification} onChange={_onChangeJustification}
                            multiline
                            required
                            resizable={false}
                            onRenderSuffix={onRenderSuffix}
                            styles={{
                                root: {minHeight: '120px'},
                                fieldGroup: [{
                                    borderColor: '#C2C2C2'
                                }],
                                suffix: {
                                    position: 'absolute',
                                    top: '-9px',
                                    right: '17px',
                                    backgroundColor: '#ECECEC',
                                    padding: '8px 4px',
                                    border: '1px solid #C2C2C2',
                                    borderRadius: '4px',
                                    textAlign: 'center',
                                    fontSize: '12px',
                                    zIndex: 10
                                },
                            }}
                        />
                    </StackItem>
                    <StackItem className={styles.CancelModalButtons}>
                        <PrimaryButton text={strings.requestModalButtonConfirm} disabled={state.justification === "" || state.justification === undefined} onClick={_onClickConfirmCancel}/>
                        <DefaultButton text={strings.requestModalButtonCancel} onClick={() => {setState({...state, isCancelModalOpen: false, justification: ""})}}/>
                    </StackItem>
                </Stack>
            </Modal>
            <Modal
                isOpen={state.isDeleteModalOpen}
                onDismiss={() => setState({...state, isDeleteModalOpen: false})}
                className={styles.CancelModal}
            >
                <Stack className={styles.DeleteModalContainer}>
                    <StackItem className={styles.DeleteModalTitleRow}>
                        <div>
                            <span className={styles.DeleteModalTitle}>
                                {strings.requestModalDeleteDraftTitle}
                            </span>
                        </div>
                        <IconButton
                            style={{marginLeft: '40px'}}
                            iconProps={{iconName: "Cancel"}}
                            ariaLabel="Close popup modal"
                            onClick={() => setState({...state, isDeleteModalOpen: false, justification: ""})}
                        />
                    </StackItem>
                    <StackItem className={styles.ReminderModalTextRow}>
                        <span>
                            {strings.requestModalDeleteDraftBody}
                        </span>
                    </StackItem>
                    <StackItem className={styles.CancelModalButtons}>
                        <PrimaryButton text={strings.requestModalButtonConfirm} onClick={_onDeleteDraftRequest}/>
                        <DefaultButton text={strings.requestModalButtonCancel} onClick={() => {setState({...state, isDeleteModalOpen: false})}}/>
                    </StackItem>
                </Stack>
            </Modal>
        </Stack>
    )
}

export default FilteredDetailsListWithPagination;
